import React, { useEffect, useRef, useState } from 'react';

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string;
  /** Called once typing pauses (and right away on Enter, paste or clear). */
  onValueChange: (value: string) => void;
  delay?: number;
  /** Pasting several lines or tab-separated cells (e.g. a column of SKUs) joins them with commas. */
  pasteAsList?: boolean;
}

/**
 * A text input that keeps what's typed in its own state, so each keystroke re-renders only the input,
 * not the (often very large) page that filters on the value.
 */
export const DebouncedInput = React.forwardRef<HTMLInputElement, DebouncedInputProps>(
  ({ value, onValueChange, delay = 250, pasteAsList = false, onKeyDown, onPaste, ...rest }, ref) => {
    const [text, setText] = useState(value);
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const latest = useRef(onValueChange);
    latest.current = onValueChange;
    const sent = useRef(value);

    useEffect(() => {
      if (value !== sent.current) {
        sent.current = value;
        setText(value);
      }
    }, [value]);

    useEffect(() => () => clearTimeout(timer.current), []);

    const commit = (v: string) => {
      clearTimeout(timer.current);
      if (v === sent.current) return;
      sent.current = v;
      latest.current(v);
    };

    const update = (v: string, now = false) => {
      setText(v);
      clearTimeout(timer.current);
      if (now || v === '') commit(v);
      else timer.current = setTimeout(() => commit(v), delay);
    };

    return (
      <input
        ref={ref}
        {...rest}
        value={text}
        onChange={(e) => update(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(text);
          onKeyDown?.(e);
        }}
        onPaste={(e) => {
          onPaste?.(e);
          if (e.defaultPrevented || !pasteAsList) return;
          const pasted = e.clipboardData.getData('text');
          if (!/[\r\n\t]/.test(pasted.trim())) return;
          e.preventDefault();
          const list = pasted.split(/[\r\n\t,]+/).map((s) => s.trim()).filter(Boolean).join(', ');
          const input = e.currentTarget;
          const start = input.selectionStart ?? text.length;
          const end = input.selectionEnd ?? text.length;
          const before = text.slice(0, start).replace(/[\s,]+$/, '');
          const after = text.slice(end).replace(/^[\s,]+/, '');
          update([before, list, after].filter(Boolean).join(', '), true);
        }}
      />
    );
  },
);
DebouncedInput.displayName = 'DebouncedInput';
